import React, { useState, useEffect } from 'react';
import { api, endpoints } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { ReactComponent as AlertIcon } from '../../icons/alert.svg';
import '../admin/OrganizationsPage.css'; // Aynı CSS'i kullan

const NotificationsPage = () => {
  const { logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    notification_type: 'info',
    target_type: 'all',
    target_organization: '',
    target_role: '',
    expires_at: '',
    is_active: true
  });

  useEffect(() => {
    fetchNotifications();
    fetchOrganizations();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(endpoints.admin.notifications.list);
      const notificationsList = data.results || data || [];
      setNotifications(notificationsList);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.response?.data?.error || 'Uyarılar yüklenemedi');
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const data = await api.get(endpoints.admin.organizations.list);
      const orgList = data.results || data || [];
      setOrganizations(orgList);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  const handleCreateNotification = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = { ...formData };
      
      // Clean up payload based on target_type
      if (payload.target_type !== 'organization') {
        delete payload.target_organization;
      }
      if (payload.target_type !== 'role') {
        delete payload.target_role;
      }
      if (!payload.expires_at) {
        delete payload.expires_at;
      }

      await api.post(endpoints.admin.notifications.create, payload);
      setShowCreateModal(false);
      resetForm();
      fetchNotifications();
    } catch (err) {
      console.error('Error creating notification:', err);
      setError(err.response?.data?.error || 'Uyarı oluşturulamadı');
    }
  };

  const handleUpdateNotification = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = { ...formData };
      
      // Clean up payload based on target_type
      if (payload.target_type !== 'organization') {
        delete payload.target_organization;
      }
      if (payload.target_type !== 'role') {
        delete payload.target_role;
      }
      if (!payload.expires_at) {
        delete payload.expires_at;
      }

      await api.patch(endpoints.admin.notifications.detail(selectedNotification.id), payload);
      setShowEditModal(false);
      setSelectedNotification(null);
      resetForm();
      fetchNotifications();
    } catch (err) {
      console.error('Error updating notification:', err);
      setError(err.response?.data?.error || 'Uyarı güncellenemedi');
    }
  };

  const handleToggleActive = async (notification) => {
    try {
      await api.patch(endpoints.admin.notifications.detail(notification.id), {
        is_active: !notification.is_active
      });
      fetchNotifications();
    } catch (err) {
      console.error('Error toggling notification status:', err);
      setError(err.response?.data?.error || 'Durum güncellenemedi');
    }
  };

  const openEditModal = (notification) => {
    setSelectedNotification(notification);
    setFormData({
      title: notification.title,
      message: notification.message,
      notification_type: notification.notification_type,
      target_type: notification.target_type,
      target_organization: notification.target_organization || '',
      target_role: notification.target_role || '',
      expires_at: notification.expires_at ? notification.expires_at.split('T')[0] : '',
      is_active: notification.is_active
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (notification) => {
    setSelectedNotification(notification);
    setShowDeleteModal(true);
  };

  const handleDeleteNotification = async () => {
    if (!selectedNotification) return;
    try {
      await api.delete(endpoints.admin.notifications.detail(selectedNotification.id));
      setShowDeleteModal(false);
      setSelectedNotification(null);
      fetchNotifications();
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError(err.response?.data?.error || 'Uyarı silinemedi');
      setShowDeleteModal(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      notification_type: 'info',
      target_type: 'all',
      target_organization: '',
      target_role: '',
      expires_at: '',
      is_active: true
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Statistics
  const stats = {
    total: notifications.length,
    active: notifications.filter(n => n.is_active).length,
    info: notifications.filter(n => n.notification_type === 'info').length,
    warning: notifications.filter(n => n.notification_type === 'warning').length,
    error: notifications.filter(n => n.notification_type === 'error').length,
    success: notifications.filter(n => n.notification_type === 'success').length,
  };

  // Filtered notifications
  const filteredNotifications = notifications.filter(notification =>
    notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    notification.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTypeIcon = (type) => {
    switch(type) {
      case 'info': return 'ℹ️';
      case 'warning': return '⚠️';
      case 'error': return '❌';
      case 'success': return '✅';
      default: return '📢';
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      info: 'info-badge',
      warning: 'warning-badge',
      error: 'error-badge',
      success: 'success-badge'
    };
    return badges[type] || 'info-badge';
  };

  const getTargetText = (notification) => {
    if (notification.target_type === 'all') return 'Tüm Kullanıcılar';
    if (notification.target_type === 'organization') {
      const org = organizations.find(o => o.id === notification.target_organization);
      return org ? `Organizasyon: ${org.name}` : 'Belirli Organizasyon';
    }
    if (notification.target_type === 'role') {
      const roles = {
        super_admin: 'Süper Admin',
        org_admin: 'Org Admin',
        org_user: 'Kullanıcı'
      };
      return `Rol: ${roles[notification.target_role] || notification.target_role}`;
    }
    return '-';
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>Sistem Uyarıları</h1>
        </div>
        <div className="loading-state">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>🔔 Sistem Uyarıları</h1>
          <p className="page-subtitle">Kullanıcılara sistem genelinde uyarılar gönderin</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          + Yeni Uyarı Oluştur
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📢</div>
          <div className="stat-content">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Toplam Uyarı</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.active}</div>
            <div className="stat-label">Aktif</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">ℹ️</div>
          <div className="stat-content">
            <div className="stat-value">{stats.info}</div>
            <div className="stat-label">Bilgi</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <div className="stat-value">{stats.warning}</div>
            <div className="stat-label">Uyarı</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="table-controls">
        <input
          type="text"
          className="search-input"
          placeholder="Uyarı ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Notifications Table */}
      <div className="table-container">
        {filteredNotifications.length === 0 ? (
          <div className="empty-state">
            <AlertIcon style={{ width: '64px', height: '64px', opacity: 0.3 }} />
            <h3>Henüz uyarı yok</h3>
            <p>Yeni uyarı oluşturmak için yukarıdaki butonu kullanın</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Tip</th>
                <th>Başlık</th>
                <th>Mesaj</th>
                <th>Hedef</th>
                <th>Oluşturulma</th>
                <th>Son Geçerlilik</th>
                <th>Durum</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredNotifications.map((notification) => (
                <tr key={notification.id}>
                  <td>
                    <span className={`notification-type-badge ${getTypeBadge(notification.notification_type)}`}>
                      {getTypeIcon(notification.notification_type)} {notification.notification_type}
                    </span>
                  </td>
                  <td><strong>{notification.title}</strong></td>
                  <td>
                    <div style={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {notification.message}
                    </div>
                  </td>
                  <td>{getTargetText(notification)}</td>
                  <td>{new Date(notification.created_at).toLocaleDateString('tr-TR')}</td>
                  <td>
                    {notification.expires_at 
                      ? new Date(notification.expires_at).toLocaleDateString('tr-TR')
                      : '∞ Süresiz'
                    }
                  </td>
                  <td>
                    <span className={`status-badge ${notification.is_active ? 'active' : 'inactive'}`}>
                      <span className="status-indicator"></span>
                      {notification.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon edit"
                        onClick={() => openEditModal(notification)}
                        title="Düzenle"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon delete"
                        onClick={() => openDeleteModal(notification)}
                        title="Sil"
                      >
                        🗑️
                      </button>
                      <button
                        className={`btn btn-${notification.is_active ? 'secondary' : 'success'}`}
                        style={{ fontSize: '12px', padding: '6px 12px' }}
                        onClick={() => handleToggleActive(notification)}
                      >
                        {notification.is_active ? 'Devre Dışı' : 'Aktif Et'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Yeni Uyarı Oluştur</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateNotification}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Başlık *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      placeholder="Uyarı başlığı"
                    />
                  </div>

                  <div className="form-group">
                    <label>Tip *</label>
                    <select
                      name="notification_type"
                      value={formData.notification_type}
                      onChange={handleChange}
                      required
                    >
                      <option value="info">ℹ️ Bilgi</option>
                      <option value="warning">⚠️ Uyarı</option>
                      <option value="error">❌ Hata</option>
                      <option value="success">✅ Başarı</option>
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Mesaj *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                      placeholder="Uyarı mesajı"
                    ></textarea>
                  </div>

                  <div className="form-group">
                    <label>Hedef *</label>
                    <select
                      name="target_type"
                      value={formData.target_type}
                      onChange={handleChange}
                      required
                    >
                      <option value="all">Tüm Kullanıcılar</option>
                      <option value="organization">Belirli Organizasyon</option>
                      <option value="role">Belirli Rol</option>
                    </select>
                  </div>

                  {formData.target_type === 'organization' && (
                    <div className="form-group">
                      <label>Organizasyon *</label>
                      <select
                        name="target_organization"
                        value={formData.target_organization}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Seçiniz...</option>
                        {organizations.map(org => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {formData.target_type === 'role' && (
                    <div className="form-group">
                      <label>Rol *</label>
                      <select
                        name="target_role"
                        value={formData.target_role}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Seçiniz...</option>
                        <option value="super_admin">Süper Admin</option>
                        <option value="org_admin">Organizasyon Admin</option>
                        <option value="org_user">Kullanıcı</option>
                      </select>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Son Geçerlilik Tarihi</label>
                    <input
                      type="date"
                      name="expires_at"
                      value={formData.expires_at}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <small>Boş bırakırsanız süresiz olur</small>
                  </div>

                  <div className="form-group">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '28px' }}>
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleChange}
                        id="is_active"
                      />
                      <label htmlFor="is_active" style={{ margin: 0 }}>
                        Uyarı aktif
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Oluştur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedNotification && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Uyarıyı Düzenle</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateNotification}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Başlık *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Tip *</label>
                    <select
                      name="notification_type"
                      value={formData.notification_type}
                      onChange={handleChange}
                      required
                    >
                      <option value="info">ℹ️ Bilgi</option>
                      <option value="warning">⚠️ Uyarı</option>
                      <option value="error">❌ Hata</option>
                      <option value="success">✅ Başarı</option>
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Mesaj *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                    ></textarea>
                  </div>

                  <div className="form-group">
                    <label>Hedef *</label>
                    <select
                      name="target_type"
                      value={formData.target_type}
                      onChange={handleChange}
                      required
                    >
                      <option value="all">Tüm Kullanıcılar</option>
                      <option value="organization">Belirli Organizasyon</option>
                      <option value="role">Belirli Rol</option>
                    </select>
                  </div>

                  {formData.target_type === 'organization' && (
                    <div className="form-group">
                      <label>Organizasyon *</label>
                      <select
                        name="target_organization"
                        value={formData.target_organization}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Seçiniz...</option>
                        {organizations.map(org => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {formData.target_type === 'role' && (
                    <div className="form-group">
                      <label>Rol *</label>
                      <select
                        name="target_role"
                        value={formData.target_role}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Seçiniz...</option>
                        <option value="super_admin">Süper Admin</option>
                        <option value="org_admin">Organizasyon Admin</option>
                        <option value="org_user">Kullanıcı</option>
                      </select>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Son Geçerlilik Tarihi</label>
                    <input
                      type="date"
                      name="expires_at"
                      value={formData.expires_at}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="form-group">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '28px' }}>
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleChange}
                        id="is_active_edit"
                      />
                      <label htmlFor="is_active_edit" style={{ margin: 0 }}>
                        Uyarı aktif
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedNotification && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>⚠️ Uyarıyı Sil</h2>
              <button className="close-btn" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ 
                background: '#fee2e2', 
                border: '1px solid #fecaca', 
                borderRadius: '8px', 
                padding: '16px', 
                marginBottom: '20px' 
              }}>
                <p style={{ margin: 0, color: '#991b1b', fontWeight: 500 }}>
                  ⚠️ Bu işlem geri alınamaz!
                </p>
              </div>
              <p style={{ fontSize: '15px', color: '#4b5563', marginBottom: '16px' }}>
                <strong>{selectedNotification.title}</strong> uyarısını silmek üzeresiniz.
              </p>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                Uyarı bilgileri:
              </p>
              <ul style={{ fontSize: '14px', color: '#6b7280', marginLeft: '20px' }}>
                <li>Tip: <strong>{selectedNotification.notification_type}</strong></li>
                <li>Hedef: <strong>{getTargetText(selectedNotification)}</strong></li>
                <li>Durum: <strong>{selectedNotification.is_active ? 'Aktif' : 'Pasif'}</strong></li>
              </ul>
              <p style={{ fontSize: '14px', color: '#dc2626', fontWeight: 600, marginTop: '16px' }}>
                Silmek istediğinizden emin misiniz?
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                İptal
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteNotification}>
                🗑️ Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
